import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getFunctions } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';

const firebaseConfig = {
	apiKey: 'AIzaSyBZ7IY5w8lzz8Apx9uHd4EGQc1JmXre0UM',
	authDomain: 'boesensteinproject.firebaseapp.com',
	projectId: 'boesensteinproject',
	storageBucket: 'boesensteinproject.firebasestorage.app',
	messagingSenderId: '415183649588',
	appId: '1:415183649588:web:40f4736f860978688b0361'
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

const functions = getFunctions(app, "europe-central2");

let analyticsInstance = null;
let performanceInstance = null;
let analyticsInitPromise = null;
let appCheckInstance = null;
let appCheckInitPromise = null;

const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const appCheckDebugToken = process.env.REACT_APP_APPCHECK_DEBUG_TOKEN;

const canUseBrowserApis = () => typeof window !== 'undefined' && typeof document !== 'undefined';

if (process.env.NODE_ENV === 'development' && canUseBrowserApis()) {
	// If App Check enforcement is enabled, every device needs a valid App Check token.
	// Using `true` generates a per-device debug token, which is annoying for multi-device dev.
	// Setting a fixed token lets you register it once in the Firebase Console and reuse it.
	window.FIREBASE_APPCHECK_DEBUG_TOKEN = appCheckDebugToken || true;
}

export const initializeAnalyticsAndPerformance = () => {
	if (!canUseBrowserApis()) {
		return Promise.resolve({ analytics: null, performance: null });
	}

	// Keep unit tests lightweight and avoid Jest ESM parsing issues from optional SDKs.
	if (process.env.NODE_ENV === 'test') {
		return Promise.resolve({ analytics: null, performance: null });
	}

	if (analyticsInstance && performanceInstance) {
		return Promise.resolve({ analytics: analyticsInstance, performance: performanceInstance });
	}

	if (!analyticsInitPromise) {
		analyticsInitPromise = (async () => {
			try {
				const analyticsModule = await import('firebase/analytics');
				const supported = await analyticsModule.isSupported();
				if (supported) {
					analyticsInstance = analyticsModule.getAnalytics(app);
				}
			} catch (error) {
				console.warn('Analytics support unavailable', error);
			}

			try {
				const performanceModule = await import('firebase/performance');
				performanceInstance = performanceModule.getPerformance(app);
			} catch (error) {
				console.warn('Performance monitoring unavailable', error);
			}

			return { analytics: analyticsInstance, performance: performanceInstance };
		})();
	}

	return analyticsInitPromise;
};

export const initializeRecaptchaAppCheck = () => {
	if (!canUseBrowserApis() || !recaptchaSiteKey) {
		if (!recaptchaSiteKey) {
			console.warn('Missing REACT_APP_RECAPTCHA_SITE_KEY: Firebase App Check not initialized.');
		}
		return Promise.resolve(null);
	}

	if (appCheckInstance) {
		return Promise.resolve(appCheckInstance);
	}

	if (!appCheckInitPromise) {
		appCheckInitPromise = new Promise(resolve => {
			try {
				appCheckInstance = initializeAppCheck(app, {
					provider: new ReCaptchaV3Provider(recaptchaSiteKey),
					isTokenAutoRefreshEnabled: true
				});
			} catch (error) {
				console.warn('Failed to initialize Firebase App Check', error);
				appCheckInstance = null;
			}
			resolve(appCheckInstance);
		});
	}

	return appCheckInitPromise;
};

export {
	app,
	auth,
	db,
	googleProvider,
	storage,
	functions,
	analyticsInstance as analytics,
	performanceInstance as performance,
	appCheckInstance as appCheck
};
